import { TrackBuffer } from "../utils/TrackBuffer";
import { AggregatedTracker } from "./AggregatedTracker";
import { ConsoleTracker } from "./ConsoleTracker";

export type DOMAIN_TYPE = "WITH_SUBDOMAINS" | "WITHOUT_SUBDOMAINS";

export type WEB_SOURCE = "TOTAL" | "MOBILEWEB" | "DESKTOP";

export type TConversionData = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
};
export type TrackModel = {
  category: string | Array<string>;
  action: string;
  cid?: string;
  name: string;
  trackId: string;
  location: string;
  value?: number;
  ab_test_name?: string;
  ab_test_value?: string;
  conversionData?: TConversionData;
};

/**
 * Each application should implement its own closed set of tracking events e.g.
 * https://gitlab.similarweb.io/pro-frontend/sw-track-management/-/blob/master/website/client/src/constants/EventCategoriesAndActions.ts
 */
export type TrackingEvent = string;
export type CustomAttribute = string;

/**
 * Common Interface for Trackers
 */
export interface ITrack {
  /**
   * track an Event
   * @param category event category
   * @param action event action
   * @param name event name
   * @param value a number value
   */
  trackEvent: (category: string, action: string, name: string, value?: number, ab_test_name?: string, ab_test_value?: string) => void;

  /**
   * Allows tracking with custom attributes, if a certain attribute already exists [from page view / global attribute] it'll ignore the
   * custom one and display a warning message
   * Example:
   *    Global attributes : { userName: 123, email: "luna@gmail.com" }
   *    CustomAttributes: { keywordsAmount: 12, segments: 14, userName: 342 }
   *    Result: it'll send an event without the new userName
   *        i.e { keywordsAmount: 12, segments: 14 }
   *        and a warning message will be displayed to notify the FED.
   * */
  dispatchTrackEvent: (
    event: TrackingEvent,
    action: string,
    eventName: string,
    customAttributes?: Record<CustomAttribute, Primitive>,
  ) => void;

  /**
   * track an Event
   * @param trackPayload event category
   */
  trackEvent2: (trackPayload: TrackModel) => void;

  /**
   * track a navigation
   * @param urlParams
   */
  // TODO: (dannyr) change the interface to a generic type
  trackPageView: (urlParams?: ICustomDimensionData) => void;

  /**
   * health check is called when the system starts
   * use this to check that everything has loaded
   */
  healthCheck: () => void;

  /**
   * run any custom action
   * @param action
   * @param args
   */
  runCustomAction: (action: string, ...args) => void;

  /**
   * get the tracking buffer
   */
  getBuffer: () => TrackBuffer;
}

export interface IDimensions {
  [name: string]: number;
}

/**
 * custom dimensions and their IDs
 * @type {{string: number}}
 */
export const CustomDimensions: IDimensions = {
  path: 1,
  lang: 2,
  is_sw_user: 3,
  query: 4,
  entity: 5,
  filters: 6,
  category: 7,
  identity: 8,
  custom_data: 9,
};

export const FieldNameToDimensionNumMap: IDimensions = {
  language: 2,
  lang: 2,
  is_sw_user: 3,
  is_sw_users: 3,
  country: 4,
  date_range: 4,
  store: 4,
  device_os: 4,
  not_permitted: 4,
  entity_id: 5,
  entity_name: 5,
  entity_type: 5,
  domain_type: 6,
  web_source: 6,
  tab: 6,
  app_mode: 6,
  page_number: 6,
  main_category: 7,
  sub_category: 7,
  custom_category_id: 7,
  custom_category_name: 7,
  subscription_id: 8,
  base_product: 8,
  user_id: 8,
  account_id: 8,
  email: 8,
  sw_extention: 8,
  page: 9,
  ab_test_name: 9,
  ab_test_value: 9,
  contact_us_subject: 9,
  connected_accounts: 9,
  di_types: 9,
  is_small_site: 9,
  cookies: 9,
  sgId: 11,
  page_sfcmpid: 13,
  section: 21,
  sub_section: 22,
  sub_sub_section: 23,
  page_id: 24,
  cid: 24, // not used in pageview
};

export interface ICustomDimensionQuery {
  country?: number;
  store?: string;
  device_os?: string;
  date_range?: string;
  order_by?: string;
  not_permitted?: boolean;
}

interface ICustomDimensionCategoryInfo {
  main_category: string;
  sub_category: string;
}

interface ICustomDimensionCustomCategoryInfo {
  custom_category_id: string | number;
  custom_category_name: string;
  is_shared_custom_category?: boolean;
  domains_amount?: number;
  segments_amount?: number;
  entities_amount?: number;
}

export type ICustomDimensionCategory = ICustomDimensionCategoryInfo | ICustomDimensionCustomCategoryInfo;

export interface ICustomDimensionEntity {
  entity_name?: string;
  entity_id?: string;
  entity_type?: string;
}

export interface ICustomDimensionPath {
  section: string;
  sub_section: string;
  sub_sub_section: string;
  page_id: string;
  sidebar_version?: string;
}

export interface ICustomDimensionIdentity {
  subscription_id: number;
  base_product: string;
  user_id: number;
  account_id: number;
  email: string;
  role?: string;
}

export interface ICustomDimensionFilters {
  domain_type?: DOMAIN_TYPE;
  web_source?: WEB_SOURCE;
  tab?: string;
  app_mode?: string;
  page_number?: number;
}

export type ICustomDimensionCustomData = any;

/**
 * Custom Dimensions Data Object
 */
export interface ICustomDimensionData {
  path: ICustomDimensionPath;
  lang: string;
  is_sw_user: boolean;
  query?: ICustomDimensionQuery;
  entity?: ICustomDimensionEntity;
  filters?: ICustomDimensionFilters;
  category?: ICustomDimensionCategory;
  identity?: ICustomDimensionIdentity;
  custom_data?: ICustomDimensionCustomData;
}

export function trackerWithConsole(tracker: ITrack, name: string): AggregatedTracker {
  return new AggregatedTracker([tracker, new ConsoleTracker(name)]);
}
export type Primitive = string | number | boolean;
export type SerializableObject = Record<string, Primitive>;
